import { useState, useEffect } from "react";
import {
  Box,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../theme";
import Header from "../components/Header";
import { API_BASE_URL } from "../config/api";

const formatDate = (value) => {
  if (!value) return "N/A";
  try {
    const date = new Date(value);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    const dateString = String(value).split("T")[0];
    return dateString || value;
  }
};

const formatDiscountCell = (val) => {
  if (val === null || val === undefined) return "—";
  if (typeof val === "number") return String(val);
  return String(val);
};

const emptyForm = () => ({
  name: "",
  value: "",
  discountPercent: 20,
  status: true,
  airport_parking: 30,
  car_hire: 2,
  lounges: 2,
  airport_transfer: 5,
  hotels: "",
});

/** Map API row into dialog form state (string fields for controlled inputs). */
const formFromRow = (row) => {
  const pd = row.productDiscounts || {};
  const pdStr = (key) => {
    const v = pd[key];
    if (v === null || v === undefined) return "";
    return String(v);
  };
  return {
    name: row.name ?? "",
    value: row.value ?? "",
    discountPercent: row.discountPercent != null ? String(row.discountPercent) : "20",
    status: row.status === true,
    airport_parking: pdStr("airport_parking") || "0",
    car_hire: pdStr("car_hire") || "0",
    lounges: pdStr("lounges") || "0",
    airport_transfer: pdStr("airport_transfer") || "0",
    hotels: pd.hotels === null || pd.hotels === undefined ? "" : String(pd.hotels),
  };
};

const parseOptionalNumber = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const parseRequiredNumber = (v) => {
  const n = parseOptionalNumber(v);
  return n === null ? 0 : n;
};

const Discount = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchOffers = async () => {
    try {
      setLoading(true);
      setListError(null);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/admin/offer/getAll`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch offers");
      }

      const data = await response.json();
      if (data.subCode === 200 && Array.isArray(data.items)) {
        setRows(
          data.items.map((item) => ({
            ...item,
            id: item._id,
          }))
        );
      } else {
        setListError("Failed to load offers: " + (data.message || "Unknown error"));
      }
    } catch (err) {
      setListError(err.message);
      console.error("Offers fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const handleOpenAdd = () => {
    setDialogMode("add");
    setEditingId(null);
    setForm(emptyForm());
    setFormError("");
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setDialogMode("edit");
    setEditingId(row._id);
    setForm(formFromRow(row));
    setFormError("");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (submitting) return;
    setDialogOpen(false);
    setFormError("");
    setDialogMode("add");
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.value.trim()) {
      setFormError("Name and code are required.");
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      const token = localStorage.getItem("authToken");
      const productDiscounts = {
        airport_parking: parseRequiredNumber(form.airport_parking),
        car_hire: parseRequiredNumber(form.car_hire),
        lounges: parseRequiredNumber(form.lounges),
        airport_transfer: parseRequiredNumber(form.airport_transfer),
        hotels:
          form.hotels === "" || form.hotels === null || form.hotels === undefined
            ? null
            : parseOptionalNumber(form.hotels),
      };

      if (dialogMode === "edit" && editingId) {
        const body = {
          name: form.name.trim(),
          value: form.value.trim(),
          productDiscounts,
          status: form.status,
        };

        const response = await fetch(`${API_BASE_URL}/admin/offer/edit/${editingId}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.subCode !== 200) {
          throw new Error(data.message || "Failed to update offer");
        }
      } else {
        const body = {
          name: form.name.trim(),
          value: form.value.trim(),
          discountPercent: parseRequiredNumber(form.discountPercent),
          status: form.status,
          productDiscounts,
        };

        const response = await fetch(`${API_BASE_URL}/admin/offer/add`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.subCode !== 200) {
          throw new Error(data.message || "Failed to add offer");
        }
      }

      setDialogOpen(false);
      setFormError("");
      setDialogMode("add");
      setEditingId(null);
      await fetchOffers();
    } catch (err) {
      setFormError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const pd = (row, key) => row.productDiscounts?.[key];

  const columns = [
    {
      field: "createdAt",
      headerName: "Create Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value),
    },
    // {
    //   field: "_id",
    //   headerName: "ID",
    //   flex: 1,
    //   minWidth: 280,
    //   renderCell: (params) => (
    //     <Box sx={{ whiteSpace: "normal", wordBreak: "break-all", lineHeight: 1.5 }}>
    //       {params.value}
    //     </Box>
    //   ),
    // },
    {
      field: "name",
      headerName: "Name",
      flex: 1.3,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "value",
      headerName: "Code",
      flex: 0.9,
      minWidth: 120,
    },
    {
      field: "pd_hotels",
      headerName: "Hotels",
      flex: 0.7,
      minWidth: 90,
      valueGetter: (params) => pd(params.row, "hotels"),
      renderCell: (params) => formatDiscountCell(pd(params.row, "hotels")),
    },
    {
      field: "pd_lounges",
      headerName: "Lounges",
      flex: 0.7,
      minWidth: 90,
      valueGetter: (params) => pd(params.row, "lounges"),
      renderCell: (params) => formatDiscountCell(pd(params.row, "lounges")),
    },
    {
      field: "pd_car_hire",
      headerName: "Car hire",
      flex: 0.8,
      minWidth: 90,
      valueGetter: (params) => pd(params.row, "car_hire"),
      renderCell: (params) => formatDiscountCell(pd(params.row, "car_hire")),
    },
    {
      field: "pd_airport_parking",
      headerName: "Airport parking",
      flex: 1,
      minWidth: 130,
      valueGetter: (params) => pd(params.row, "airport_parking"),
      renderCell: (params) => formatDiscountCell(pd(params.row, "airport_parking")),
    },
    {
      field: "pd_airport_transfer",
      headerName: "Airport transfer",
      flex: 1,
      minWidth: 130,
      valueGetter: (params) => pd(params.row, "airport_transfer"),
      renderCell: (params) => formatDiscountCell(pd(params.row, "airport_transfer")),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 90,
      renderCell: (params) => (params.value === true ? "Active" : "Inactive"),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.75,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={() => handleOpenEdit(params.row)}
        >
          Edit
        </Button>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value),
    },
  ];

  if (loading) {
    return <Box m="20px">Loading offers...</Box>;
  }

  return (
    <Box m="20px">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        mb="10px"
      >
        <Box flex={1} minWidth={220}>
          <Header title="DISCOUNTS" subtitle="Offers and promo codes" />
        </Box>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleOpenAdd}
          sx={{ flexShrink: 0, mt: { xs: 0, sm: "10px" } }}
        >
          Add Discount
        </Button>
      </Box>

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      )}

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
            py: "8px",
            fontSize: "1rem",
            lineHeight: 1.5,
            color: colors.grey[100],
          },
          "& .MuiDataGrid-row": {
            minHeight: "90px !important",
            maxHeight: "none !important",
            height: "auto !important",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? `${colors.primary[500]} !important`
                  : `rgba(0, 0, 0, 0.04) !important`,
            },
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
            fontSize: "1rem",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: "1rem",
            },
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          checkboxSelection
          rows={rows}
          columns={columns}
          autoHeight={false}
          disableRowSelectionOnClick
          disableColumnResize={false}
          initialState={{
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
        />
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ component: "form", onSubmit: handleSubmit }}
      >
        <DialogTitle>{dialogMode === "edit" ? "Edit discount" : "Add discount"}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Offer details
          </Typography>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mb: 1 }}
          />
          <TextField
            margin="dense"
            label="Code"
            fullWidth
            required
            value={form.value}
            onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
            sx={{ mb: 1 }}
          />
          {dialogMode === "add" && (
            <TextField
              margin="dense"
              label="Discount %"
              type="number"
              fullWidth
              inputProps={{ step: "any", min: 0 }}
              value={form.discountPercent}
              onChange={(e) => setForm((f) => ({ ...f, discountPercent: e.target.value }))}
              sx={{ mb: 1 }}
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked }))}
                color="secondary"
              />
            }
            label="Active"
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Product discounts (%)
          </Typography>
          <TextField
            margin="dense"
            label="Airport parking"
            type="number"
            fullWidth
            inputProps={{ step: "any", min: 0 }}
            value={form.airport_parking}
            onChange={(e) => setForm((f) => ({ ...f, airport_parking: e.target.value }))}
            sx={{ mb: 1 }}
          />
          <TextField
            margin="dense"
            label="Car hire"
            type="number"
            fullWidth
            inputProps={{ step: "any", min: 0 }}
            value={form.car_hire}
            onChange={(e) => setForm((f) => ({ ...f, car_hire: e.target.value }))}
            sx={{ mb: 1 }}
          />
          <TextField
            margin="dense"
            label="Lounges"
            type="number"
            fullWidth
            inputProps={{ step: "any", min: 0 }}
            value={form.lounges}
            onChange={(e) => setForm((f) => ({ ...f, lounges: e.target.value }))}
            sx={{ mb: 1 }}
          />
          <TextField
            margin="dense"
            label="Airport transfer"
            type="number"
            fullWidth
            inputProps={{ step: "any", min: 0 }}
            value={form.airport_transfer}
            onChange={(e) => setForm((f) => ({ ...f, airport_transfer: e.target.value }))}
            sx={{ mb: 1 }}
          />
          <TextField
            margin="dense"
            label="Hotels (leave empty for none)"
            type="number"
            fullWidth
            inputProps={{ step: "any", min: 0 }}
            value={form.hotels}
            onChange={(e) => setForm((f) => ({ ...f, hotels: e.target.value }))}
            helperText="Empty sends null for hotels"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="secondary" disabled={submitting}>
            {submitting ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Discount;
